import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ASSIGN_FIELD_MAX,
  assignFormSchema,
  readAssignError,
  type AssignFormValues,
  type AssignRequest,
  type AssignResponse,
  type FactoryUsbKey,
  type FactoryUsbStatus,
} from '@/schemas/factory-usb';

/** Operations the operator may run against a registry record. */
export type FactoryUsbAction = 'revoke' | 'retire' | 'revokeSession';

export interface FactoryUsbTableProps {
  keys: FactoryUsbKey[];
  /** Rejects on failure; the table keeps the dialog open and shows the failure itself. */
  onAction: (action: FactoryUsbAction, key: FactoryUsbKey) => Promise<void>;
  /** Assigns an INACTIVE key to a person; rejects on failure, the dialog reports it. */
  onAssign: (request: AssignRequest) => Promise<AssignResponse>;
}

/** Status → i18n key. Kept here so the page can reuse the very same labels. */
export const FACTORY_USB_STATUS_LABEL_KEYS: Record<FactoryUsbStatus, string> = {
  INACTIVE: 'factoryUsb.statusInactive',
  ACTIVE: 'factoryUsb.statusActive',
  REVOKED: 'factoryUsb.statusRevoked',
  RETIRED: 'factoryUsb.statusRetired',
};

const STATUS_BADGE_VARIANTS: Record<
  FactoryUsbStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  INACTIVE: 'outline',
  ACTIVE: 'default',
  REVOKED: 'destructive',
  RETIRED: 'secondary',
};

const DIALOG_KEYS: Record<
  FactoryUsbAction,
  { title: string; description: string; danger: boolean }
> = {
  revoke: {
    title: 'factoryUsb.revokeTitle',
    description: 'factoryUsb.revokeDescription',
    danger: true,
  },
  retire: {
    title: 'factoryUsb.retireTitle',
    description: 'factoryUsb.retireDescription',
    danger: false,
  },
  revokeSession: {
    title: 'factoryUsb.sessionRevokeTitle',
    description: 'factoryUsb.sessionRevokeDescription',
    danger: false,
  },
};

interface PendingAction {
  action: FactoryUsbAction;
  key: FactoryUsbKey;
}

/** Failure of an assignment as the dialog shows it: a code it knows, plus the key it names. */
type AssignFailure = ReturnType<typeof readAssignError>;

/** Fields the assignment dialog asks for, in the order the operator fills them. */
const ASSIGN_FIELDS: readonly { name: keyof AssignFormValues; labelKey: string; max: number }[] = [
  {
    name: 'employee_id',
    labelKey: 'factoryUsb.assignFieldEmployeeId',
    max: ASSIGN_FIELD_MAX.employee_id,
  },
  {
    name: 'employee_name',
    labelKey: 'factoryUsb.assignFieldEmployeeName',
    max: ASSIGN_FIELD_MAX.employee_name,
  },
  {
    name: 'requested_by',
    labelKey: 'factoryUsb.assignFieldRequestedBy',
    max: ASSIGN_FIELD_MAX.requested_by,
  },
];

const ASSIGN_INPUT_CLASS =
  'h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors ' +
  'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/** Our own text, never the raw server message — the blocking key id is all we take from it. */
function assignFailureView(failure: AssignFailure): { key: string; keyId?: string } {
  if (failure.code === 'KEY_NOT_INACTIVE') return { key: 'factoryUsb.assignErrorNotInactive' };
  if (failure.code === 'EMPLOYEE_ALREADY_HAS_ACTIVE_KEY' && failure.keyId) {
    return { key: 'factoryUsb.assignErrorAlreadyActive', keyId: failure.keyId };
  }
  return { key: 'factoryUsb.actionError' };
}

interface AssignDialogProps {
  target: FactoryUsbKey;
  busy: boolean;
  failure: AssignFailure | null;
  onCancel: () => void;
  onSubmit: (request: AssignRequest) => void;
}

/**
 * Assignment of an INACTIVE key to a person. Mounted only while a key is picked, so every open
 * starts from an empty form; the key itself comes from the row, never from the operator.
 */
function AssignDialog({ target, busy, failure, onCancel, onSubmit }: AssignDialogProps) {
  const { t } = useTranslation();
  const form = useForm<AssignFormValues>({
    resolver: zodResolver(assignFormSchema),
    defaultValues: { employee_id: '', employee_name: '', requested_by: '' },
  });
  const { errors } = form.formState;
  const failureView = failure ? assignFailureView(failure) : null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('factoryUsb.assignTitle')}</DialogTitle>
          <DialogDescription>
            {t('factoryUsb.assignDescription', { keyId: target.keyId })}
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          noValidate
          onSubmit={form.handleSubmit((values) => onSubmit({ key_id: target.keyId, ...values }))}
        >
          {ASSIGN_FIELDS.map(({ name, labelKey, max }) => {
            const fieldError = errors[name];
            return (
              <div className="flex flex-col gap-1" key={name}>
                <label className="text-sm font-medium" htmlFor={`factory-usb-assign-${name}`}>
                  {t(labelKey)}
                </label>
                <input
                  id={`factory-usb-assign-${name}`}
                  className={ASSIGN_INPUT_CLASS}
                  autoComplete="off"
                  disabled={busy}
                  {...form.register(name)}
                />
                {fieldError ? (
                  <p className="text-xs text-destructive">
                    {t(fieldError.message ?? 'factoryUsb.wizardValidationRequired', { max })}
                  </p>
                ) : null}
              </div>
            );
          })}

          {failureView ? (
            <p className="text-sm text-destructive" role="alert">
              {t(failureView.key, { keyId: failureView.keyId })}
            </p>
          ) : null}

          <DialogFooter>
            <Button variant="outline" type="button" onClick={onCancel} disabled={busy}>
              {t('factoryUsb.assignCancel')}
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? t('factoryUsb.assignProcessing') : t('factoryUsb.assignConfirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY = '—';

const DATE_FORMATTER = new Intl.DateTimeFormat('ru-RU', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

/** Dates come from the server as ISO strings; anything unparsable is shown verbatim. */
function formatDate(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : DATE_FORMATTER.format(date);
}

/** Fingerprints are long hex — the full value stays in the `title` tooltip. */
function shortenFingerprint(value: string | null | undefined): string {
  if (!value) return EMPTY;
  return value.length > 16 ? `${value.slice(0, 16)}…` : value;
}

/**
 * Registry of issued Factory USB keys: the operator operations on live keys and the assignment
 * step that turns an issued, still unassigned medium into one person's credential.
 * Terminal operations (revoke / retire) are irreversible, so they go through a confirm dialog.
 */
export function FactoryUsbTable({ keys, onAction, onAssign }: FactoryUsbTableProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [busy, setBusy] = useState<FactoryUsbAction | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [assignTarget, setAssignTarget] = useState<FactoryUsbKey | null>(null);
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignFailure, setAssignFailure] = useState<AssignFailure | null>(null);
  const [assigned, setAssigned] = useState<AssignResponse | null>(null);

  const requestAction = useCallback((action: FactoryUsbAction, key: FactoryUsbKey) => {
    setFailure(null);
    setAssigned(null);
    setPending({ action, key });
  }, []);

  const requestAssign = useCallback((key: FactoryUsbKey) => {
    setAssignFailure(null);
    setAssigned(null);
    setAssignTarget(key);
  }, []);

  const closeAssign = useCallback(() => {
    if (assignBusy) return;
    setAssignTarget(null);
    setAssignFailure(null);
  }, [assignBusy]);

  const submitAssign = useCallback(
    async (request: AssignRequest) => {
      setAssignBusy(true);
      setAssignFailure(null);
      try {
        const response = await onAssign(request);
        setAssignTarget(null);
        setAssigned(response);
      } catch (error) {
        setAssignFailure(readAssignError(error));
      } finally {
        setAssignBusy(false);
      }
    },
    [onAssign]
  );

  const closeDialog = useCallback(() => {
    if (busy !== null) return;
    setPending(null);
    setFailure(null);
  }, [busy]);

  const confirmAction = useCallback(async () => {
    if (!pending) return;
    setBusy(pending.action);
    setFailure(null);
    try {
      await onAction(pending.action, pending.key);
      setPending(null);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(null);
    }
  }, [onAction, pending]);

  const columns = useMemo<ColumnDef<FactoryUsbKey>[]>(() => {
    const rowBusy = busy !== null;

    return [
      {
        accessorKey: 'keyId',
        header: t('factoryUsb.columnKeyId'),
        cell: (info) => <span className="font-mono text-xs">{info.getValue<string>()}</span>,
      },
      {
        accessorKey: 'employeeName',
        header: t('factoryUsb.columnEmployee'),
        cell: (info) => {
          const row = info.row.original;
          if (!row.employeeName) {
            return (
              <span className="text-muted-foreground">{t('factoryUsb.employeeUnassigned')}</span>
            );
          }
          return (
            <div className="flex flex-col">
              <span>{row.employeeName}</span>
              {row.employeeId ? (
                <span className="text-xs text-muted-foreground">{row.employeeId}</span>
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: t('factoryUsb.columnStatus'),
        cell: (info) => {
          const status = info.getValue<FactoryUsbStatus>();
          return (
            <Badge variant={STATUS_BADGE_VARIANTS[status]}>
              {t(FACTORY_USB_STATUS_LABEL_KEYS[status])}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'usbFingerprint',
        header: t('factoryUsb.columnFingerprint'),
        cell: (info) => (
          <span className="font-mono text-xs" title={info.row.original.usbFingerprint ?? undefined}>
            {shortenFingerprint(info.getValue<string | null>())}
          </span>
        ),
      },
      {
        accessorKey: 'issuedAt',
        header: t('factoryUsb.columnIssuedAt'),
        cell: (info) => <span className="text-sm">{formatDate(info.getValue<string>())}</span>,
      },
      {
        accessorKey: 'expiresAt',
        header: t('factoryUsb.columnExpiresAt'),
        cell: (info) => (
          <span className="text-sm">{formatDate(info.getValue<string | null>())}</span>
        ),
      },
      {
        accessorKey: 'lastUsedAt',
        header: t('factoryUsb.columnLastUsed'),
        cell: (info) => (
          <span className="text-sm">{formatDate(info.getValue<string | null>())}</span>
        ),
      },
      {
        id: 'actions',
        header: () => <span className="block text-right">{t('factoryUsb.columnActions')}</span>,
        cell: (info) => {
          const row = info.row.original;

          return (
            <div className="flex flex-wrap justify-end gap-2">
              {row.status === 'INACTIVE' ? (
                <Button size="sm" disabled={rowBusy} onClick={() => requestAssign(row)}>
                  {t('factoryUsb.actionAssign')}
                </Button>
              ) : null}
              {/* revoke/retire are ACTIVE-only: the server rejects any non-ACTIVE key (400) */}
              {row.status === 'ACTIVE' ? (
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={rowBusy}
                  onClick={() => requestAction('revoke', row)}
                >
                  {t('factoryUsb.actionRevoke')}
                </Button>
              ) : null}
              {row.status === 'ACTIVE' ? (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={rowBusy}
                  onClick={() => requestAction('retire', row)}
                >
                  {t('factoryUsb.actionRetire')}
                </Button>
              ) : null}
              {/* Session revoke is ACTIVE-only too, and only when the key actually holds a
                  live session (#156): the server returns a 404 otherwise. */}
              {row.status === 'ACTIVE' ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={rowBusy || row.activeSession === null}
                  title={
                    row.activeSession === null
                      ? t('factoryUsb.actionRevokeSessionNoSession')
                      : undefined
                  }
                  onClick={() => requestAction('revokeSession', row)}
                >
                  {t('factoryUsb.actionRevokeSession')}
                </Button>
              ) : null}
            </div>
          );
        },
      },
    ];
  }, [busy, requestAction, requestAssign, t]);

  const table = useReactTable({
    data: keys,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const dialogKeys = pending ? DIALOG_KEYS[pending.action] : null;

  return (
    <>
      {assigned ? (
        <p className="text-sm text-muted-foreground" role="status">
          {t('factoryUsb.assignSuccess', {
            employeeName: assigned.employeeName ?? assigned.keyId,
          })}
        </p>
      ) : null}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialogKeys ? t(dialogKeys.title) : null}</DialogTitle>
            <DialogDescription>
              {dialogKeys && pending
                ? t(dialogKeys.description, { keyId: pending.key.keyId })
                : null}
            </DialogDescription>
          </DialogHeader>

          {failure ? (
            <p className="text-sm text-destructive" role="alert">
              {t('factoryUsb.actionError')}
            </p>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={busy !== null}>
              {t('factoryUsb.actionCancel')}
            </Button>
            <Button
              variant={dialogKeys?.danger ? 'destructive' : 'default'}
              onClick={() => void confirmAction()}
              disabled={busy !== null}
            >
              {busy !== null ? t('factoryUsb.actionProcessing') : t('factoryUsb.actionConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {assignTarget ? (
        <AssignDialog
          target={assignTarget}
          busy={assignBusy}
          failure={assignFailure}
          onCancel={closeAssign}
          onSubmit={(request) => void submitAssign(request)}
        />
      ) : null}
    </>
  );
}
